/**
 * Lien Waivers comment text generation.
 *
 * Kept free of component imports so the wording can be unit-tested directly.
 *
 * Each covered payapp (G702/draw) has its own waiver status. One comment is generated for the
 * section: when every payapp shares a status it reads as a single plural statement, and when they
 * differ the compliant ones are stated and the deficient ones are named, assembled from the
 * conditional fragments in the client spec.
 */

export type LienWaiverStatus = 'Unconditional' | 'Conditional' | 'No Waiver';

export interface DrawLienWaiver {
  drawId: string;
  name: string;
  status: LienWaiverStatus;
}

/**
 * "A" / "A and B" / "A, B and C".
 *
 * No Oxford comma — matches the client's Excel output verbatim ("the previous Site Work,
 * Lightbridge and Dutch Bros Applications").
 */
const joinWithAnd = (parts: string[]): string => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

/**
 * Single-payapp wording. These are the originals the section shipped with, kept verbatim so a
 * report covering one payapp produces exactly the text it did before multi-draw.
 *
 * `LIEN_WAIVERS_SINGLE_CONDITIONAL_COMMENT` carries a `[G702 Name]` placeholder that is replaced
 * with the payapp name.
 */
export const LIEN_WAIVERS_SINGLE_CONDITIONAL_COMMENT =
  'We were provided with an executed and notarized Waiver and Release of Lien from the Contractor referring to the correct Project and/or Borrower. However, we note that the Release is conditional upon receipt of the current Application for Payment or is unclear. We have no documentation confirming that the Contractor has actually received payment from the Borrower making this Lien Release effective. We leave the funding without documentation stating that the Contractor has actually received payment from the Borrower on the previous [G702 Name] Application to the discretion of the Lender and its counsel.';

export const LIEN_WAIVERS_SINGLE_NO_WAIVER_COMMENT =
  'We did not receive a copy of any Lien Waiver this period, and leave funding of the current application for payment without an Unconditional Lien Waiver to the discretion of the Lender. We will request an executed and notarized Unconditional Waiver and Release of Lien from the Contractor along with the next payment application, confirming that they have been paid for this first application.';

export const LIEN_WAIVERS_SINGLE_ABOVE_TABLE_COMMENT =
  'We were provided with an executed and notarized Unconditional Waiver and Release of Lien from the Contractor as follows:';

export const LIEN_WAIVERS_SINGLE_UNCONDITIONAL_COMMENT =
  'The document refers to the correct Project and/or Borrower, acknowledges full payment to the Contractor for the previous Application for Payment, and subsequent payment by the Contractor to all Subcontractors and Suppliers.';

const MULTI_ABOVE_TABLE_COMMENT =
  'We were provided with executed and notarized Unconditional Waivers and Releases of Lien from the Contractors as follows:';

const ALL_NO_WAIVER_COMMENT =
  'We did not receive a copy of any Lien Waiver this period, and leave funding of the current applications for payment without ' +
  'Unconditional Lien Waivers to the discretion of the Lender. We will request an executed and notarized Unconditional Waiver and ' +
  'Release of Lien from each Contractor along with the next payment applications, confirming that they have been paid for these ' +
  'first applications.';

const ALL_UNCONDITIONAL_COMMENT =
  'The documents refer to the correct Project and/or Borrower, acknowledge full payment to the Contractors for the previous ' +
  'Applications for Payment, and subsequent payment by the Contractors to all Subcontractors and Suppliers.';

/** The "documents refer to the correct Project…" clause, lowercased for use mid-sentence. */
const UNCONDITIONAL_CLAUSE_LOWER =
  'the documents refer to the correct Project and/or Borrower, acknowledge full payment to the Contractors for the previous ' +
  'Applications for Payment, and subsequent payment by the Contractors to all Subcontractors and Suppliers.';

const buildAllConditionalComment = (waivers: DrawLienWaiver[]): string => {
  const names = joinWithAnd(waivers.map(w => w.name));
  const applicationWord = waivers.length === 1 ? 'Application' : 'Applications';
  return (
    'We were provided with executed and notarized Waivers and Releases of Lien from the Contractors referring to the correct ' +
    'Project and/or Borrower. However, we note that the Releases are conditional upon receipt of the current Application for ' +
    'Payment or are unclear. We have no documentation confirming that the Contractors have actually received payment from the ' +
    'Borrower making these Lien Releases effective. We leave the funding without documentation stating that the Contractors have ' +
    `actually received payment from the Borrower on the previous ${names} ${applicationWord} to the discretion of the Lender and its counsel.`
  );
};

/** "the A Application" / "the A and B Applications" */
const namedApplications = (waivers: DrawLienWaiver[]): string =>
  `${joinWithAnd(waivers.map(w => w.name))} ${waivers.length === 1 ? 'Application' : 'Applications'}`;

/**
 * Closing sentence naming what is outstanding. Either half is dropped when that group is empty, so
 * a mix of unconditional + missing waivers doesn't claim there are conditional receipts too.
 *
 * "Contractor(s)" agrees with the number of conditional applications named, since that clause is
 * about the contractors who were meant to have received payment.
 */
const buildClosingSentence = (conditional: DrawLienWaiver[], noWaiver: DrawLienWaiver[]): string => {
  const contractorClause =
    conditional.length === 1
      ? 'We leave the funding without documentation stating that the Contractor has actually received payment from the Borrower on '
      : 'We leave the funding without documentation stating that the Contractors have actually received payment from the Borrower on ';

  if (conditional.length > 0 && noWaiver.length > 0) {
    return (
      contractorClause +
      `the previous ${namedApplications(conditional)} and with the missing Waiver on the ${namedApplications(noWaiver)} to the ` +
      'discretion of the Lender and its counsel.'
    );
  }
  if (conditional.length > 0) {
    return contractorClause + `the previous ${namedApplications(conditional)} to the discretion of the Lender and its counsel.`;
  }
  return (
    `We leave the funding with the missing Waiver on the ${namedApplications(noWaiver)} to the discretion of the ` +
    'Lender and its counsel.'
  );
};

/**
 * The intro line printed ABOVE the waiver tables.
 *
 * Only unconditional waivers are "provided as follows", so this is empty when none are. Per the
 * client's Excel it stays singular while any payapp falls short and only turns plural once every
 * payapp is unconditional.
 */
export const buildLienWaiversAboveTableComment = (waivers: DrawLienWaiver[]): string => {
  const unconditional = waivers.filter(w => w.status === 'Unconditional');
  if (unconditional.length === 0) return '';
  if (waivers.length > 1 && unconditional.length === waivers.length) return MULTI_ABOVE_TABLE_COMMENT;
  return LIEN_WAIVERS_SINGLE_ABOVE_TABLE_COMMENT;
};

export const buildLienWaiversComment = (waivers: DrawLienWaiver[]): string => {
  if (waivers.length === 0) return '';

  // One payapp keeps the original singular wording, so single-draw reports are unchanged.
  if (waivers.length === 1) {
    const only = waivers[0];
    if (only.status === 'Unconditional') return LIEN_WAIVERS_SINGLE_UNCONDITIONAL_COMMENT;
    if (only.status === 'No Waiver') return LIEN_WAIVERS_SINGLE_NO_WAIVER_COMMENT;
    // Collapse the leading space too when the name is blank, so we don't leave a double space
    // where the name would have been (e.g. "the previous  Application").
    return LIEN_WAIVERS_SINGLE_CONDITIONAL_COMMENT.replace(/\s*\[G702 Name\]/, only.name ? ` ${only.name}` : '');
  }

  const unconditional = waivers.filter(w => w.status === 'Unconditional');
  const conditional = waivers.filter(w => w.status === 'Conditional');
  const noWaiver = waivers.filter(w => w.status === 'No Waiver');

  // Every payapp in the same state — one statement, nothing named as an exception.
  if (noWaiver.length === waivers.length) return ALL_NO_WAIVER_COMMENT;
  if (conditional.length === waivers.length) return buildAllConditionalComment(waivers);
  if (unconditional.length === waivers.length) return ALL_UNCONDITIONAL_COMMENT;

  // Statuses differ — assemble the spec's conditional fragments.
  const parts: string[] = [];

  // "Except for the following," qualifies the acknowledgement of payment, so per the client's Excel
  // it appears only when a Waiver was actually received but was conditional. A missing Waiver is not
  // an exception to that acknowledgement — those cases open with the plain statement.
  if (unconditional.length > 0) {
    parts.push(conditional.length > 0 ? `Except for the following, ${UNCONDITIONAL_CLAUSE_LOWER}` : ALL_UNCONDITIONAL_COMMENT);
  }

  // The shortfall sentence: conditional waivers, then missing ones, joined by ", and" when both.
  const shortfallClauses: string[] = [];
  if (conditional.length > 0) {
    shortfallClauses.push(
      conditional.length === 1
        ? `The Waiver we received from the ${namedApplications(conditional)} was conditional upon and did not acknowledge receipt of payment`
        : `The Waivers we received from the ${namedApplications(conditional)} were conditional upon and did not acknowledge receipt of payment`,
    );
  }
  if (noWaiver.length > 0) {
    const opensSentence = shortfallClauses.length === 0;
    const subject = opensSentence ? 'We' : 'we';
    shortfallClauses.push(`${subject} have not received a lien waiver on the ${namedApplications(noWaiver)}`);
  }
  if (shortfallClauses.length > 0) {
    parts.push(`${shortfallClauses.join(', and ')}.`);
  }

  parts.push(buildClosingSentence(conditional, noWaiver));

  return parts.join(' ');
};
